import FileAnswerContent from '@app/src/components/FileAnswerContent'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import VerificationButtons from '@app/src/pages/Questionnaire/Accessor/Actions/VerificationButtons'
import AnswerSourcesPanel from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AnswerSourcesPanel'
import { AiAnswerBlock } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AiAnswerBlock'
import { CopyAnswerButton } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/CopyAnswerButton'
import DiscardAiAnswerButton from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/DiscardAiAnswerButton'
import OldScrapedAnswerCitations from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/OldScrapedAnswerCitations'
import { NumberDisplayWithDeviation } from '@app/src/pages/Questionnaire/Accessor/Report/NumberDisplayWithDeviation'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { AnswerStatus, ExternalVerificationStatus } from '@app/src/types/resourceExplorer'
import { equalsCaseInsensitive } from '@app/src/utils/helpersTs'
import { QuestionTypes } from '@app/src/wf-constants'
import { VisibilityOffOutlined } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import type { Palette } from '@mui/material/styles'
import { makeStyles, useTheme } from '@mui/styles'
import React from 'react'
import { useIntl } from 'react-intl'
import CoordinateAnswerDisplay from '../../Provider/Report/CoordinateAnswerDisplay'
import SkippedQuestionCard from '../../Provider/Report/SkippedQuestionCard'
import { getResponseValueTypes } from '../../utils'
import VerificationStatus from '../../VerificationStatus'

const useStyles = makeStyles({
  value: {
    wordBreak: 'break-word',
  },
})

export interface ResponseValueProps {
  item: ResponseItem
  questionId: number
  questionType: string
  questionDescription: string
  unitSymbol: string | null
}

const getBgColor = (item: ResponseItem, palette: Palette) => {
  const isSomeExternalStatusCorrectionNeeded = item.verifications?.some(
    x => x.externalVerificationStatus === ExternalVerificationStatus.CorrectionNeeded,
  )
  if (isSomeExternalStatusCorrectionNeeded) return palette.warning.light
  if (item.answerStatus !== AnswerStatus.Answered || item.cannotAnswer) return palette.grey['100']
  return palette.common.white
}

const getAnswerLength = (item: ResponseItem, questionType: string) => {
  if (equalsCaseInsensitive(questionType, QuestionTypes.Options)) return item.selectedOptions?.join(', ').length ?? 0
  if (equalsCaseInsensitive(questionType, QuestionTypes.File)) {
    const fileNameLength = item.uploadReferences?.[0]?.originalFileName?.length ?? 0
    const expiresAtLength = item.expiresAt ? item.expiresAt.toString().length : 0
    return fileNameLength + expiresAtLength
  }
  return item.answer?.toString().length ?? 0
}

const SHORT_ANSWER_THRESHOLD = 80

const ResponseValue = ({
  item,
  questionType,
  questionDescription,
  unitSymbol,
  questionId,
}: ResponseValueProps): JSX.Element => {
  const { formatMessage } = useIntl()
  const classes = useStyles()
  const { displayAsOptions, selectedOptions, hasValue, displayAsFile, displayAsCoordinates } = getResponseValueTypes(
    questionType,
    item,
  )
  const { response, request, isResponseFetching } = useReport()
  const { palette } = useTheme()
  const hasWriteAccess = useWriteAccess()

  const displayValue = (): React.ReactNode => {
    if (item?.answerStatus !== AnswerStatus.Answered) {
      return (
        <SkippedQuestionCard
          answerStatus={item?.answerStatus}
          estimatedAnswerAt={item?.estimatedAnswerAt}
          isScraped={item?.isScraped}
          comment={item?.comment}
        />
      )
    }

    if (displayAsFile) {
      return <FileAnswerContent item={item} showExpiresAtText />
    }

    if (displayAsOptions) {
      if (selectedOptions?.length) {
        return <Typography>{selectedOptions?.join(', ')}</Typography>
      }
    }

    if (displayAsCoordinates) {
      return <CoordinateAnswerDisplay coordinates={item.coordinates ?? []} />
    }

    if (hasValue) {
      if (equalsCaseInsensitive(questionType, QuestionTypes.Number)) {
        return <NumberDisplayWithDeviation answer={item.answer} unitSymbol={unitSymbol} questionId={questionId} />
      }
      return <Typography className={classes.value}>{item.answer || ''}</Typography>
    }

    if (response?.isScraped && item.cannotAnswer) {
      return (
        <Stack direction="row" spacing={2} alignItems="center" color="text.secondary">
          <VisibilityOffOutlined />
          <Typography color="text.secondary">{formatMessage({ id: 'aiScreening.answerPanel.noEvidence' })}</Typography>
        </Stack>
      )
    }

    return <Typography>{formatMessage({ id: 'reporting.noResponse' })}</Typography>
  }

  const isScrapedAndAnswered = response?.isScraped && item.answerStatus === AnswerStatus.Answered
  const isShortAnswer = getAnswerLength(item, questionType) <= SHORT_ANSWER_THRESHOLD
  const showShortAnswerControls = hasWriteAccess && isShortAnswer
  const showLongAnswerControls = hasWriteAccess && !isShortAnswer

  const answerText = String((displayAsOptions ? selectedOptions?.join(', ') : item.answer) ?? '')
  const reviewButtons = hasWriteAccess && item.answerStatus !== AnswerStatus.Excluded && (
    <VerificationButtons item={item} questionDescription={questionDescription} isLoading={isResponseFetching} />
  )

  if (isScrapedAndAnswered) {
    return (
      <>
        <AiAnswerBlock
          actions={
            hasWriteAccess && (
              <>
                {reviewButtons}
                <CopyAnswerButton text={answerText} />
                <DiscardAiAnswerButton responseItemId={item.id} responseId={response?.id ?? 0} />
              </>
            )
          }
        >
          {displayValue()}
        </AiAnswerBlock>
        {item.scrapedAnswer?.citations?.length ? (
          <Box px={2.5}>
            <OldScrapedAnswerCitations
              citations={item.scrapedAnswer.citations}
              provider={request?.subscriptions?.[0]?.target}
            />
          </Box>
        ) : null}
        {item.scrapedAnswer?.searchResults && <AnswerSourcesPanel scrapedAnswer={item.scrapedAnswer} />}
        <VerificationStatus {...item} isLoading={isResponseFetching} />
      </>
    )
  }

  return (
    <>
      <Stack px={3} py={4} bgcolor={getBgColor(item, palette)}>
        <Box display="flex" alignItems="flex-end" sx={{ gap: isShortAnswer ? 4 : 0 }}>
          <Stack pl={2} direction="row" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{displayValue()}</Box>
          </Stack>
          {showShortAnswerControls && (
            <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
              <VerificationButtons
                item={item}
                questionDescription={questionDescription}
                isLoading={isResponseFetching}
              />
            </Stack>
          )}
        </Box>
        {showLongAnswerControls && (
          <Stack direction="row" spacing={2} alignSelf="flex-end" mt={2}>
            {item.answerStatus !== AnswerStatus.Excluded && (
              <VerificationButtons
                item={item}
                questionDescription={questionDescription}
                isLoading={isResponseFetching}
              />
            )}
          </Stack>
        )}
      </Stack>
      <VerificationStatus {...item} isLoading={isResponseFetching} />
    </>
  )
}

export default ResponseValue
